import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'

import { Button } from '@/components/ui/button'
import { Form, FormControl, FormField, FormItem } from '@/components/ui/form'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { SettingsFormFooter, SettingsSection } from '@/containers/Settings/SettingsLayout'
import { addressRequirements, cartPriceModes } from '@/interfaces/eshop'
import API from '@/lib/api'
import { extractErrorMessage } from '@/lib/api/errorUtils'

const schema = z.object({
  eshopPriceMode: z.enum(cartPriceModes),
  addressRequirement: z.enum(addressRequirements),
})

type EshopSettingsFormData = z.infer<typeof schema>

export function EshopSettingsForm({
  defaultValues,
  onSuccess,
}: {
  defaultValues: Partial<EshopSettingsFormData>
  onSuccess: () => void
}) {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'settings.eshop',
  })
  const { t: tg } = useTranslation(undefined, {
    keyPrefix: 'generic',
  })

  const form = useForm<EshopSettingsFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      eshopPriceMode: defaultValues.eshopPriceMode ?? 'live',
      addressRequirement: defaultValues.addressRequirement ?? 'always',
    },
  })

  const onSubmit = (data: EshopSettingsFormData) =>
    API.patch('/api/v2/settings', {
      eshopPriceMode: data.eshopPriceMode,
      addressRequirement: data.addressRequirement,
    })
      .then(() => {
        toast.success(t('success'))
        form.reset(data)
        onSuccess()
      })
      .catch((error: unknown) => {
        toast.error(t('error'), {
          description: extractErrorMessage(error),
        })
      })

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <FormField
          control={form.control}
          name="eshopPriceMode"
          render={({ field }) => (
            <SettingsSection
              title={t('priceMode.label')}
              description="Co se stane s cenou v košíku, když ji mezitím změníte v katalogu."
            >
              <FormItem>
                <FormControl>
                  <OptionRadioGroup
                    name="eshopPriceMode"
                    value={field.value}
                    onChange={field.onChange}
                    options={cartPriceModes.map((mode) => ({
                      value: mode,
                      label: t(`priceMode.options.${mode}.label`),
                      description: t(`priceMode.options.${mode}.description`),
                    }))}
                  />
                </FormControl>
              </FormItem>
            </SettingsSection>
          )}
        />

        <FormField
          control={form.control}
          name="addressRequirement"
          render={({ field }) => (
            <SettingsSection
              title={t('addressRequirement.label')}
              description="Kdy musí zákazník v objednávce vyplnit doručovací adresu."
            >
              <FormItem>
                <FormControl>
                  <OptionRadioGroup
                    name="addressRequirement"
                    value={field.value}
                    onChange={field.onChange}
                    options={addressRequirements.map((mode) => ({
                      value: mode,
                      label: t(`addressRequirement.options.${mode}.label`),
                      description: t(`addressRequirement.options.${mode}.description`),
                    }))}
                  />
                </FormControl>
              </FormItem>
            </SettingsSection>
          )}
        />

        <SettingsFormFooter className="mt-0">
          <Button type="submit" disabled={form.formState.isSubmitting}>
            {tg('button.save')}
          </Button>
        </SettingsFormFooter>
      </form>
    </Form>
  )
}

/** Radio volby v ohraničeném seznamu s popisem. */
const OptionRadioGroup = ({
  name,
  value,
  onChange,
  options,
}: {
  name: string
  value: string
  onChange: (value: string) => void
  options: { value: string; label: string; description: string }[]
}) => (
  <RadioGroup value={value} onValueChange={onChange} className="gap-0 divide-y overflow-hidden rounded-lg border">
    {options.map((option) => (
      <label
        key={option.value}
        htmlFor={`${name}-${option.value}`}
        className="flex cursor-pointer items-start gap-3 px-4 py-3 transition-colors hover:bg-muted has-[[data-state=checked]]:bg-muted"
      >
        <RadioGroupItem value={option.value} id={`${name}-${option.value}`} className="mt-0.5" />
        <span className="space-y-0.5">
          <span className="block text-sm font-medium">{option.label}</span>
          <span className="block text-sm text-muted-foreground">{option.description}</span>
        </span>
      </label>
    ))}
  </RadioGroup>
)
